import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Link,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  WarningAmber as WarningAmberIcon,
  Save as SaveIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { getHotelsPendingSfOnboard, approveHotelSfOnboard } from '../api/adminPanelApi';

const formatCreatedAt = (raw) => {
  if (!raw) return '–';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const hasLatLng = (row) => {
  if (row?.has_location === true) return true;
  const v = row?.lat_lng || row?.location_point;
  if (v == null) return false;
  const s = String(v).trim();
  if (!s || s === ',' || s === '0,0' || s === 'null') return false;
  return true;
};

const normLoc = (v) => String(v ?? '').trim();

const isLocationDirty = (row, draft) => {
  const origPoint = normLoc(row.location_point);
  const origEmbed = normLoc(row.location);
  const draftPoint = normLoc(draft?.location_point);
  const draftEmbed = normLoc(draft?.location);
  return draftPoint !== origPoint || draftEmbed !== origEmbed;
};

const isHttpUrl = (raw) => /^https?:\/\//i.test(normLoc(raw));

const SfOnboardApprovals = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { hotel, type: 'approve'|'reject' }
  const [locationDrafts, setLocationDrafts] = useState({}); // hotelId -> { location_point, location }
  const [savingLocationId, setSavingLocationId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const debounceRef = useRef(null);

  const getDraft = (row) => {
    const id = String(row.hotel_id);
    return (
      locationDrafts[id] || {
        location_point: row.location_point || '',
        location: row.location || '',
      }
    );
  };

  const setDraftField = (hotelId, field, value) => {
    const id = String(hotelId);
    setLocationDrafts((prev) => ({
      ...prev,
      [id]: {
        location_point: prev[id]?.location_point ?? '',
        location: prev[id]?.location ?? '',
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const syncDraftsFromList = useCallback((hotels, { reset = false } = {}) => {
    setLocationDrafts((prev) => {
      if (reset) {
        const next = {};
        (hotels || []).forEach((h) => {
          next[String(h.hotel_id)] = {
            location_point: h.location_point || '',
            location: h.location || '',
          };
        });
        return next;
      }
      const next = { ...prev };
      (hotels || []).forEach((h) => {
        const id = String(h.hotel_id);
        if (!next[id]) {
          next[id] = {
            location_point: h.location_point || '',
            location: h.location || '',
          };
        }
      });
      return next;
    });
  }, []);

  const fetchList = useCallback(async ({ q = '', notify = false, resetDrafts = false } = {}) => {
    setLoading(true);
    try {
      const res = await getHotelsPendingSfOnboard({
        q: q.trim() || undefined,
        limit: 100,
      });
      if (res.success) {
        const hotels = res.hotels || [];
        setList(hotels);
        syncDraftsFromList(hotels, { reset: resetDrafts || notify });
        if (notify) {
          setSnackbar({
            open: true,
            message: `Loaded ${res.count ?? hotels.length} pending hotel(s)`,
            severity: 'success',
          });
        }
      } else {
        setList([]);
        setSnackbar({
          open: true,
          message: res.error || 'Failed to load pending Stayflexi onboard hotels',
          severity: 'error',
        });
      }
    } catch (e) {
      setList([]);
      setSnackbar({ open: true, message: e.message || 'Failed to load', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [syncDraftsFromList]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const next = searchInput.trim();
    if (!next) {
      setAppliedQ('');
      fetchList({ q: '' });
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      setAppliedQ(next);
      fetchList({ q: next });
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput, fetchList]);

  const removeHotel = (hotelId) => {
    setList((prev) => prev.filter((h) => Number(h.hotel_id) !== Number(hotelId)));
    setLocationDrafts((prev) => {
      const next = { ...prev };
      delete next[String(hotelId)];
      return next;
    });
  };

  const handleConfirmDecision = async () => {
    if (!confirmAction?.hotel?.hotel_id || busyId != null) return;
    const hotel = confirmAction.hotel;
    const hotelId = hotel.hotel_id;
    const type = confirmAction.type;
    const draft = getDraft(hotel);

    setBusyId(hotelId);
    try {
      const payload = { hotel_id: hotelId };
      if (type === 'reject') {
        payload.decision = 'rejected';
      } else {
        payload.decision = 'approve';
      }
      // Optional: send location with approve/reject if drafted
      if (String(draft.location_point || '').trim()) {
        payload.location_point = String(draft.location_point).trim();
      }
      if (String(draft.location || '').trim()) {
        payload.location = String(draft.location).trim();
      }

      const res = await approveHotelSfOnboard(payload);
      if (res.success || res.skipped) {
        removeHotel(hotelId);
        setConfirmAction(null);
        setSnackbar({
          open: true,
          message:
            res.skipped
              ? `Already processed — removed from pending (${hotelId})`
              : res.message ||
                (type === 'reject'
                  ? 'Hotel rejected for Stayflexi onboard.'
                  : 'Stayflexi onboard approved and completed.'),
          severity: 'success',
        });
      } else {
        setConfirmAction(null);
        setSnackbar({
          open: true,
          message: res.error || (type === 'reject' ? 'Reject failed' : 'Approval failed'),
          severity: 'error',
        });
      }
    } catch (e) {
      setConfirmAction(null);
      setSnackbar({
        open: true,
        message: e.message || (type === 'reject' ? 'Reject failed' : 'Approval failed'),
        severity: 'error',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveLocation = async (row) => {
    if (!row?.hotel_id || savingLocationId != null || busyId != null) return;
    const draft = getDraft(row);
    const location_point = String(draft.location_point || '').trim();
    const location = String(draft.location || '').trim();

    if (!location_point && !location) {
      setSnackbar({
        open: true,
        message: 'Paste a maps URL (location_point) or embed before saving',
        severity: 'warning',
      });
      return;
    }

    setSavingLocationId(row.hotel_id);
    try {
      // Location-only: do NOT send decision
      const payload = { hotel_id: row.hotel_id };
      if (location_point) payload.location_point = location_point;
      if (location) payload.location = location;

      const res = await approveHotelSfOnboard(payload);
      if (res.success) {
        setList((prev) =>
          prev.map((h) =>
            Number(h.hotel_id) === Number(row.hotel_id)
              ? {
                  ...h,
                  location_point: location_point || h.location_point || '',
                  location: location || h.location || '',
                  has_location: Boolean(location_point || h.location_point || h.lat_lng),
                  has_embed_map: Boolean(location || h.location),
                }
              : h
          )
        );
        setLocationDrafts((prev) => ({
          ...prev,
          [String(row.hotel_id)]: {
            location_point: location_point || '',
            location: location || '',
          },
        }));
        setSnackbar({
          open: true,
          message: res.message || 'Location saved',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: res.error || 'Failed to save location',
          severity: 'error',
        });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to save location', severity: 'error' });
    } finally {
      setSavingLocationId(null);
    }
  };

  const anyBusy = busyId != null || savingLocationId != null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {loading && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200 }} />
      )}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/')} size="small" sx={{ color: '#FF7700' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#2E4057' }}>
              Stayflexi Onboard Pending
            </Typography>
            <Typography variant="caption" color="text.secondary">
              sf_onboard = no only · Approve / Reject · save maps location without onboard
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={() => fetchList({ q: appliedQ, notify: true })}
          disabled={loading || anyBusy}
        >
          Refresh
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search hotel name or hotel ID"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Paper>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 720 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 180 }}>Hotel</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }}>Address</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 340 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 64 }} align="center">Rooms</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 88 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 180 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && list.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      No hotels pending Stayflexi onboard.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {list.map((row) => {
                const hotelId = row.hotel_id;
                const roomCount = Number(row.active_room_count) || 0;
                const noRooms = roomCount === 0;
                const noCentre = !hasLatLng(row);
                const busy = busyId != null && Number(busyId) === Number(hotelId);
                const savingLoc = savingLocationId != null && Number(savingLocationId) === Number(hotelId);
                const draft = getDraft(row);
                const pointUrl = normLoc(draft.location_point);
                const dirty = isLocationDirty(row, draft);
                const savedPoint = normLoc(row.location_point);
                const hasSavedMaps = hasLatLng(row) || Boolean(savedPoint);
                const hasSavedEmbed = row.has_embed_map === true || Boolean(normLoc(row.location));

                return (
                  <TableRow key={hotelId} hover>
                    <TableCell sx={{ verticalAlign: 'top', width: 180, maxWidth: 180 }}>
                      <Typography variant="body2" fontWeight={700} fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                        {hotelId}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25, fontSize: '0.82rem', lineHeight: 1.3 }}>
                        {row.hotel_name || '–'}
                      </Typography>
                      {(noRooms || noCentre) && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {noRooms && (
                            <Chip size="small" icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />} label="No rooms" color="warning" sx={{ height: 20, fontSize: 10 }} />
                          )}
                          {noCentre && (
                            <Chip size="small" icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />} label="No location" color="warning" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', width: 120, maxWidth: 120 }}>
                      <Tooltip title={row.address || ''} placement="top" enterDelay={400}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            maxWidth: 110,
                            lineHeight: 1.35,
                            fontSize: '0.7rem',
                          }}
                        >
                          {row.address || '–'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', minWidth: 340 }}>
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          border: dirty ? '1px solid #ffb74d' : '1px solid #e6ebf0',
                          bgcolor: dirty ? '#fffaf3' : '#fafbfc',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mb: 1 }}>
                          <Chip
                            size="small"
                            label={hasSavedMaps ? 'Maps saved' : 'Maps missing'}
                            color={hasSavedMaps ? 'success' : 'default'}
                            sx={{ height: 20, fontSize: 10 }}
                          />
                          <Chip
                            size="small"
                            label={hasSavedEmbed ? 'Embed saved' : 'No embed'}
                            color={hasSavedEmbed ? 'info' : 'default'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: 10 }}
                          />
                          {dirty && (
                            <Chip size="small" label="Unsaved" color="warning" sx={{ height: 20, fontSize: 10 }} />
                          )}
                          {isHttpUrl(pointUrl) && (
                            <Link
                              href={pointUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: 12, ml: 'auto' }}
                            >
                              Open map <OpenInNewIcon sx={{ fontSize: 13 }} />
                            </Link>
                          )}
                        </Box>

                        <Stack spacing={1}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Google Maps link"
                            placeholder="Paste maps share URL"
                            value={draft.location_point}
                            onChange={(e) => setDraftField(hotelId, 'location_point', e.target.value)}
                            disabled={anyBusy}
                            helperText="Used as location_point for onboard centre"
                            FormHelperTextProps={{ sx: { mx: 0, mt: 0.25, fontSize: '0.65rem' } }}
                            InputProps={{ sx: { fontSize: 12, bgcolor: '#fff' } }}
                            InputLabelProps={{ sx: { fontSize: 12 } }}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Embed map (optional)"
                            placeholder="Paste iframe or embed URL"
                            value={draft.location}
                            onChange={(e) => setDraftField(hotelId, 'location', e.target.value)}
                            disabled={anyBusy}
                            multiline
                            minRows={1}
                            maxRows={2}
                            InputProps={{ sx: { fontSize: 12, bgcolor: '#fff' } }}
                            InputLabelProps={{ sx: { fontSize: 12 } }}
                          />
                          {dirty && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={savingLoc ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                                onClick={() => handleSaveLocation(row)}
                                disabled={anyBusy}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  bgcolor: '#FF7700',
                                  '&:hover': { bgcolor: '#e65100' },
                                }}
                              >
                                {savingLoc ? 'Saving…' : 'Save changes'}
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                disabled={anyBusy || savingLoc}
                                onClick={() =>
                                  setLocationDrafts((prev) => ({
                                    ...prev,
                                    [String(hotelId)]: {
                                      location_point: row.location_point || '',
                                      location: row.location || '',
                                    },
                                  }))
                                }
                                sx={{ textTransform: 'none', color: 'text.secondary' }}
                              >
                                Reset
                              </Button>
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ verticalAlign: 'top', width: 64 }}>
                      <Chip size="small" label={roomCount} color={noRooms ? 'warning' : 'default'} sx={{ height: 22, minWidth: 32 }} />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', width: 88, maxWidth: 88 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', lineHeight: 1.3, display: 'block' }}>
                        {formatCreatedAt(row.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top', width: 180 }}>
                      <Stack direction="column" spacing={0.75} alignItems="flex-end">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={busy && confirmAction?.type === 'approve' ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon fontSize="small" />}
                          disabled={anyBusy}
                          onClick={() => setConfirmAction({ hotel: row, type: 'approve' })}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: '#00897b',
                            '&:hover': { bgcolor: '#00695c' },
                            minWidth: 96,
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={busy && confirmAction?.type === 'reject' ? <CircularProgress size={14} color="inherit" /> : <CancelIcon fontSize="small" />}
                          disabled={anyBusy}
                          onClick={() => setConfirmAction({ hotel: row, type: 'reject' })}
                          sx={{ textTransform: 'none', fontWeight: 600, minWidth: 96 }}
                        >
                          Reject
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={!!confirmAction}
        onClose={() => {
          if (busyId != null) return;
          setConfirmAction(null);
        }}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={busyId != null}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {confirmAction?.type === 'reject' ? 'Reject Stayflexi onboard?' : 'Approve Stayflexi onboard?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {confirmAction?.type === 'reject' ? (
              <>
                Are you sure you want to <strong>reject</strong>{' '}
                <strong>{confirmAction?.hotel?.hotel_name || 'this hotel'}</strong> (
                {confirmAction?.hotel?.hotel_id})?
                <Box component="p" sx={{ mt: 1.5, mb: 0, color: 'text.secondary', fontSize: '0.875rem' }}>
                  It will leave the pending list and will not be onboarded.
                </Box>
              </>
            ) : (
              <>
                Are you sure you want to <strong>approve</strong> Stayflexi onboard for{' '}
                <strong>{confirmAction?.hotel?.hotel_name || 'this hotel'}</strong> (
                {confirmAction?.hotel?.hotel_id})?
                <Box component="p" sx={{ mt: 1.5, mb: 0, color: 'text.secondary', fontSize: '0.875rem' }}>
                  This will run the Stayflexi onboard process. Cancel if you opened this by mistake.
                </Box>
              </>
            )}
          </DialogContentText>
          {confirmAction?.type === 'approve' &&
            confirmAction.hotel &&
            (Number(confirmAction.hotel.active_room_count) === 0 || !hasLatLng(confirmAction.hotel)) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {Number(confirmAction.hotel.active_room_count) === 0 && 'This hotel has 0 active rooms. '}
                {!hasLatLng(confirmAction.hotel) && 'Location / maps link may be missing. '}
                Onboard may fail.
              </Alert>
            )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setConfirmAction(null)} disabled={busyId != null} autoFocus>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmAction?.type === 'reject' ? 'error' : 'primary'}
            onClick={handleConfirmDecision}
            disabled={busyId != null}
            sx={
              confirmAction?.type === 'approve'
                ? { bgcolor: '#00897b', '&:hover': { bgcolor: '#00695c' } }
                : undefined
            }
          >
            {busyId != null ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                {confirmAction?.type === 'reject' ? 'Rejecting…' : 'Approving…'}
              </Box>
            ) : confirmAction?.type === 'reject' ? (
              'Yes, reject'
            ) : (
              'Yes, approve'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SfOnboardApprovals;
